// Una sección del anexo técnico: título, tipo (párrafo, viñetas o tabla) y su contenido (F4b · D1; rediseño F4c).
// Sin estado de página. Subir y bajar son botones (se llega con teclado).
import { ChevronDown, ChevronUp, Plus, X } from 'lucide-react';
import type { SeccionAnexo, TipoDeSeccion } from '../../types/pricer-datec.types';
import { LIMITES_ANEXO, NOMBRE_DEL_TIPO, cambiarTipo } from '../../lib/anexo-tecnico';
import { TablaDeAnexoEditor } from './TablaDeAnexoEditor';
import { QuitarConConfirmacion } from './QuitarConConfirmacion';
import { ContadorDeLimite } from './ContadorDeLimite';
import { EstilosDelEditor } from './EstilosDelEditor';

interface Props {
  seccion: SeccionAnexo;
  /** «01», «02», … (sale del orden; no se guarda). */
  numero?: string;
  onChange: (s: SeccionAnexo) => void;
  onSubir?: () => void;
  onBajar?: () => void;
  onQuitar: () => void;
  soloLectura?: boolean;
}

export function SeccionDeAnexoEditor({
  seccion: s,
  numero,
  onChange,
  onSubir,
  onBajar,
  onQuitar,
  soloLectura,
}: Props) {
  const bloqueado = !!soloLectura;
  const rotulo = `Sección ${numero ?? ''} ${s.titulo}`.trim();
  const vinetas = s.vinetas ?? [''];

  const cambiarVineta = (i: number, v: string) =>
    onChange({ ...s, vinetas: vinetas.map((x, k) => (k === i ? v : x)) });

  return (
    <div role="group" aria-label={rotulo} className="dd-seccion" style={{ marginTop: 10 }}>
      <EstilosDelEditor />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {numero && (
          <span
            className="pd-mono"
            aria-hidden="true"
            style={{
              fontWeight: 700,
              color: 'hsl(var(--muted-foreground))',
              fontSize: 13,
              minWidth: 22,
            }}
          >
            {numero}
          </span>
        )}
        <input
          className="pd-field-input"
          aria-label={`${rotulo}: título`}
          style={{ flex: '1 1 200px', minWidth: 0, fontWeight: 600 }}
          value={s.titulo}
          maxLength={LIMITES_ANEXO.tituloDeSeccion}
          disabled={bloqueado}
          placeholder="Título de la sección"
          onChange={(e) => onChange({ ...s, titulo: e.target.value })}
        />
        <select
          className="pd-field-input"
          aria-label={`${rotulo}: forma de presentar`}
          style={{ width: 130, flex: 'none' }}
          value={s.tipo}
          disabled={bloqueado}
          onChange={(e) => onChange(cambiarTipo(s, e.target.value as TipoDeSeccion))}
        >
          {(Object.keys(NOMBRE_DEL_TIPO) as TipoDeSeccion[]).map((t) => (
            <option key={t} value={t}>
              {NOMBRE_DEL_TIPO[t]}
            </option>
          ))}
        </select>
        {!bloqueado && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginLeft: 'auto' }}>
            <button
              type="button"
              className="dd-iconbtn"
              disabled={!onSubir}
              aria-label={`${rotulo}: subir`}
              title="Subir la sección"
              onClick={onSubir}
            >
              <ChevronUp size={16} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="dd-iconbtn"
              disabled={!onBajar}
              aria-label={`${rotulo}: bajar`}
              title="Bajar la sección"
              onClick={onBajar}
            >
              <ChevronDown size={16} aria-hidden="true" />
            </button>
            <QuitarConConfirmacion
              ariaLabel={`${rotulo}: quitar`}
              pregunta="¿Quitar esta sección?"
              onConfirmar={onQuitar}
            />
          </div>
        )}
      </div>

      <textarea
        className="pd-field-input"
        style={{ marginTop: 10, lineHeight: 1.5, resize: 'vertical' }}
        rows={s.tipo === 'PARRAFO' ? 4 : 2}
        aria-label={`${rotulo}: ${s.tipo === 'PARRAFO' ? 'texto' : 'texto de introducción'}`}
        value={s.texto}
        maxLength={LIMITES_ANEXO.parrafo}
        disabled={bloqueado}
        placeholder={s.tipo === 'PARRAFO' ? 'Escribí el texto' : 'Introducción (opcional)'}
        onChange={(e) => onChange({ ...s, texto: e.target.value })}
      />
      <ContadorDeLimite largo={s.texto.length} max={LIMITES_ANEXO.parrafo} />

      {s.tipo === 'VINETAS' && (
        <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
          {vinetas.map((v, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              <span
                aria-hidden="true"
                style={{
                  color: 'hsl(var(--muted-foreground))',
                  lineHeight: '36px',
                  width: 14,
                  textAlign: 'center',
                }}
              >
                •
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <input
                  className="pd-field-input"
                  aria-label={`${rotulo}: viñeta ${i + 1}`}
                  value={v}
                  maxLength={LIMITES_ANEXO.vinetaMaxima}
                  disabled={bloqueado}
                  onChange={(e) => cambiarVineta(i, e.target.value)}
                />
                <ContadorDeLimite largo={v.length} max={LIMITES_ANEXO.vinetaMaxima} />
              </div>
              {!bloqueado && vinetas.length > 1 && (
                <button
                  type="button"
                  className="dd-iconbtn dd-peligro"
                  style={{ marginTop: 4 }}
                  aria-label={`${rotulo}: quitar la viñeta ${i + 1}`}
                  title="Quitar la viñeta"
                  onClick={() => onChange({ ...s, vinetas: vinetas.filter((_, k) => k !== i) })}
                >
                  <X size={15} aria-hidden="true" />
                </button>
              )}
            </div>
          ))}
          {!bloqueado && (
            <div>
              <button
                type="button"
                className="dd-btn dd-btn-chico dd-btn-agregar"
                disabled={vinetas.length >= LIMITES_ANEXO.vinetasMaximas}
                onClick={() => onChange({ ...s, vinetas: [...vinetas, ''] })}
              >
                <Plus size={13} aria-hidden="true" /> Viñeta
              </button>
            </div>
          )}
        </div>
      )}

      {s.tipo === 'TABLA' && s.tabla && (
        <div style={{ marginTop: 10 }}>
          <TablaDeAnexoEditor
            tabla={s.tabla}
            rotulo={rotulo}
            soloLectura={soloLectura}
            onChange={(tabla) => onChange({ ...s, tabla })}
          />
        </div>
      )}
    </div>
  );
}
